import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Modal,
  ModalContent,
  ModalCloseButton,
  MobileSearchField,
  StatusBadge,
  Textarea,
  FormField,
  Input,
  ConfirmationModal,
} from '../common';
import { Text } from '../common/Typography';
import { AddNewGuestRow } from '../common/AddNewGuestSearchRow';
import { formatPlayerName } from '../../utils/nameUtils';
import { getGenderTextColor, getGenderAvatarBg } from '../../utils/genderColors';
import { getPlayerRosterTypeMeta } from '../../utils/clubPlayerLinkDedup';
import { playerMatchesSearchQuery } from '../../utils/playerUtils';
import { preventInputBlurOnPointerDown } from '../../utils/mobileSearchInput';
import { createGuest, fetchClubDefaultStartingElo } from '../../services/guestService';
import { fetchPasteAliasesByClub, savePasteAlias } from '../../services/clubPlayerPasteAliasService';
import { ELO_CONSTANTS } from '../../utils/constants';
import ClubLeaderboardPreview from '../leaderboard/ClubLeaderboardPreview';
import {
  matchRosterPaste,
  selectPlayerForRow,
  clearPlayerForRow,
  assignSignupPlacements,
  assignLivePlacements,
  getSuggestedPlayers,
  summarizePasteRows,
  writablePasteRows,
  PASTE_PLACEMENT,
  PASTE_ROW_STATUS,
} from '../../utils/rosterPasteMatch';

function playerInitials(player) {
  return `${player?.first_name?.[0] || ''}${player?.last_name?.[0] || ''}`.toUpperCase();
}

function PlacementBadge({ row, mode }) {
  if (row.status === PASTE_ROW_STATUS.NEEDS_MATCH) {
    return (
      <StatusBadge variant="warning" size="small">
        Needs a match
      </StatusBadge>
    );
  }
  if (row.placement === PASTE_PLACEMENT.ALREADY_ON_SESSION) {
    return (
      <StatusBadge variant="info" size="small">
        Already on session
      </StatusBadge>
    );
  }
  if (row.placement === PASTE_PLACEMENT.BLOCKED) {
    return (
      <StatusBadge variant="danger" size="small">
        Session full
      </StatusBadge>
    );
  }
  if (row.placement === PASTE_PLACEMENT.WAITLIST) {
    return (
      <StatusBadge variant="warning" size="small">
        Waitlist #{row.waitlistPosition}
      </StatusBadge>
    );
  }
  if (row.placement === PASTE_PLACEMENT.CONFIRMED) {
    return (
      <StatusBadge variant="success" size="small">
        Confirmed
      </StatusBadge>
    );
  }
  if (row.placement === PASTE_PLACEMENT.ADD) {
    return (
      <StatusBadge variant="success" size="small">
        {mode === 'live' ? 'Will add' : 'Ready'}
      </StatusBadge>
    );
  }
  return null;
}

function ReviewRow({ row, selected, mode, onChoose, onClear }) {
  const pasted = row.parsed.raw;
  const matchedName = selected ? formatPlayerName(selected) : '';
  const showPastedAsSecondary = Boolean(selected && matchedName && matchedName.toLowerCase() !== pasted.toLowerCase());

  return (
    <li className="flex min-h-[44px] items-center gap-2 border-b border-border-medium px-3 py-1.5 last:border-b-0 sm:gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-baseline gap-2">
          <span
            className={`truncate text-sm font-medium ${
              selected ? getGenderTextColor(selected.gender) : 'text-text-primary'
            }`}
          >
            {selected ? matchedName : pasted}
          </span>
          <span className="shrink-0">
            <PlacementBadge row={row} mode={mode} />
          </span>
        </div>
        {selected ? (
          showPastedAsSecondary ? (
            <p className="truncate text-xs text-text-muted">Pasted as {pasted}</p>
          ) : null
        ) : (
          <p className="truncate text-xs text-text-muted">No player chosen</p>
        )}
        {row.blockedReason ? (
          <p className="truncate text-xs text-danger-dark">{row.blockedReason}</p>
        ) : null}
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <Button
          variant="ghost"
          size="small"
          className="min-h-[44px] px-2 text-xs sm:min-h-0 sm:h-8"
          onClick={onChoose}
        >
          {selected ? 'Change' : 'Choose'}
        </Button>
        {selected ? (
          <Button
            variant="ghost"
            size="small"
            className="min-h-[44px] px-2 text-xs text-text-muted sm:min-h-0 sm:h-8"
            onClick={onClear}
          >
            Clear
          </Button>
        ) : null}
      </div>
    </li>
  );
}

function GenderPick({ value, onChange, disabled }) {
  return (
    <div className="flex gap-2" role="group" aria-label="Gender">
      {[
        { id: 'male', label: 'Male' },
        { id: 'female', label: 'Female' },
      ].map(({ id, label }) => {
        const selected = value === id;
        return (
          <button
            key={id}
            type="button"
            disabled={disabled}
            onClick={() => onChange(id)}
            aria-pressed={selected}
            className={`flex-1 rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface disabled:opacity-50 ${
              selected
                ? id === 'female'
                  ? 'border-female bg-female-light/40 text-female dark:border-fuchsia-400 dark:bg-fuchsia-500/20 dark:text-fuchsia-200'
                  : 'border-male bg-male/15 text-text-primary dark:bg-sky-500/15 dark:border-sky-400/70 dark:text-sky-200'
                : 'border-border-medium bg-surface text-text-secondary hover:border-primary/40 hover:bg-surface-subtle'
            }`}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

function PlayerPickRow({ player, onPick }) {
  const label = formatPlayerName(player) || 'Player';
  const typeMeta = player.is_guest ? getPlayerRosterTypeMeta(player) : null;

  return (
    <button
      type="button"
      onMouseDown={preventInputBlurOnPointerDown}
      onClick={() => onPick(player)}
      className="flex w-full min-h-[44px] touch-manipulation items-center justify-between gap-3 rounded-lg border border-border-medium bg-surface px-3 py-2.5 text-left hover:bg-surface-subtle"
    >
      <div className="flex min-w-0 items-center gap-3">
        <div
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-medium text-white ${getGenderAvatarBg(player.gender)}`}
        >
          {playerInitials(player)}
        </div>
        <div className="min-w-0">
          <div className={`truncate font-medium ${getGenderTextColor(player.gender)}`}>{label}</div>
          {typeMeta ? (
            <StatusBadge variant={typeMeta.variant} size="small" className="mt-1">
              {typeMeta.label}
            </StatusBadge>
          ) : null}
        </div>
      </div>
      <span className="shrink-0 text-xs font-medium text-primary">Choose</span>
    </button>
  );
}

const PasteRosterModal = ({
  isOpen,
  onClose,
  mode = 'signup',
  players = [],
  getMembership,
  signedUpPlayerIds,
  sessionPlayerStatus,
  capacity = {},
  onAccept,
  clubId = null,
  seasonId = null,
  currentUser = null,
}) => {
  const [step, setStep] = useState('paste');
  const [pasteText, setPasteText] = useState('');
  const [rows, setRows] = useState([]);
  const [rosterPlayers, setRosterPlayers] = useState([]);
  const [pickingRowId, setPickingRowId] = useState(null);
  const [pickerSearch, setPickerSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [guestFirst, setGuestFirst] = useState('');
  const [guestLast, setGuestLast] = useState('');
  const [guestGender, setGuestGender] = useState('');
  const [guestMixedElo, setGuestMixedElo] = useState(ELO_CONSTANTS.DEFAULT_STARTING_ELO);
  const [guestLevelsElo, setGuestLevelsElo] = useState(ELO_CONSTANTS.DEFAULT_STARTING_ELO);
  const [clubDefaultElo, setClubDefaultElo] = useState(ELO_CONSTANTS.DEFAULT_STARTING_ELO);
  const [eloExpanded, setEloExpanded] = useState(false);
  const [showEloGuide, setShowEloGuide] = useState(false);
  const [creatingGuest, setCreatingGuest] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [aliasMap, setAliasMap] = useState(() => new Map());

  const resetState = useCallback(() => {
    setStep('paste');
    setPasteText('');
    setRows([]);
    setRosterPlayers([]);
    setPickingRowId(null);
    setPickerSearch('');
    setSubmitting(false);
    setError(null);
    setResult(null);
    setGuestFirst('');
    setGuestLast('');
    setGuestGender('');
    setGuestMixedElo(ELO_CONSTANTS.DEFAULT_STARTING_ELO);
    setGuestLevelsElo(ELO_CONSTANTS.DEFAULT_STARTING_ELO);
    setClubDefaultElo(ELO_CONSTANTS.DEFAULT_STARTING_ELO);
    setEloExpanded(false);
    setShowEloGuide(false);
    setCreatingGuest(false);
    setShowDiscardConfirm(false);
    setAliasMap(new Map());
  }, []);

  useEffect(() => {
    if (isOpen) resetState();
  }, [isOpen, resetState]);

  const placedRows = useMemo(() => {
    if (mode === 'signup') {
      return assignSignupPlacements(rows, {
        confirmedCount: capacity.confirmedCount || 0,
        maxPlayers: capacity.maxPlayers || 16,
        waitlistEnabled: capacity.waitlistEnabled !== false,
        waitlistCount: capacity.waitlistCount || 0,
        signupSelection: capacity.signupSelection,
      });
    }
    return assignLivePlacements(rows);
  }, [rows, mode, capacity]);

  const summary = useMemo(() => summarizePasteRows(placedRows), [placedRows]);
  const writable = useMemo(() => writablePasteRows(placedRows), [placedRows]);
  const playersById = useMemo(() => {
    const map = {};
    (rosterPlayers || []).forEach((player) => {
      map[player.id] = player;
    });
    return map;
  }, [rosterPlayers]);

  const pickingRow = placedRows.find((row) => row.id === pickingRowId) || null;
  const canCreateGuest = Boolean(clubId && currentUser?.id);
  const shouldConfirmDiscard = step !== 'done' && summary.needsMatch > 0;

  const pickerFiltered = useMemo(() => {
    if (!pickingRow) return { suggested: [], others: [] };
    const { suggested, others } = getSuggestedPlayers(pickingRow.parsed, rosterPlayers, {
      aliasByNormalized: aliasMap,
    });
    const term = pickerSearch.trim();
    const filterList = (list) =>
      list.filter((player) => playerMatchesSearchQuery(player, term));
    return {
      suggested: filterList(suggested),
      others: filterList(others),
    };
  }, [pickingRow, rosterPlayers, pickerSearch, aliasMap]);

  const requestClose = useCallback(() => {
    if (submitting || creatingGuest) return;
    if (shouldConfirmDiscard) {
      setShowDiscardConfirm(true);
      return;
    }
    onClose?.();
  }, [submitting, creatingGuest, shouldConfirmDiscard, onClose]);

  const confirmDiscardAndClose = useCallback(() => {
    setShowDiscardConfirm(false);
    onClose?.();
  }, [onClose]);

  const handleReview = async () => {
    setError(null);
    try {
      const aliasResult = clubId
        ? await fetchPasteAliasesByClub(clubId)
        : { success: true, map: new Map() };
      if (!aliasResult.success) {
        console.error('[PasteRosterModal] Failed to load paste aliases:', aliasResult.error);
      }
      const aliasByNormalized = aliasResult.map || new Map();
      setAliasMap(aliasByNormalized);

      const matched = matchRosterPaste({
        text: pasteText,
        players,
        getMembership,
        signedUpPlayerIds,
        sessionPlayerStatus,
        aliasByNormalized,
      });
      if (!matched.rows.length) {
        setError('Paste at least one name, one per line.');
        return;
      }
      setRows(matched.rows);
      setRosterPlayers(matched.players);
      setStep('review');
    } catch (err) {
      console.error('[PasteRosterModal] Failed to review paste:', err);
      setError(err.message || 'Failed to match names');
    }
  };

  const handleSelectPlayer = (player) => {
    if (!pickingRowId) return;
    const parsed = pickingRow?.parsed;
    setRows((prev) => selectPlayerForRow(prev, pickingRowId, player, getMembership));
    setPickingRowId(null);
    setPickerSearch('');
    setGuestFirst('');
    setGuestLast('');
    setGuestGender('');
    setEloExpanded(false);
    setShowEloGuide(false);
    setStep('review');
    if (!clubId || !parsed) return;
    savePasteAlias({ clubId, player, parsed })
      .then((result) => {
        if (result?.saved && parsed.normalized) {
          setAliasMap((prev) => {
            const next = new Map(prev);
            next.set(parsed.normalized, player.id);
            return next;
          });
        }
      })
      .catch((err) => {
        console.error('[PasteRosterModal] Failed to save paste alias:', err);
      });
  };

  const openCreateGuest = async () => {
    setError(null);
    setGuestFirst(pickingRow?.parsed?.first || '');
    setGuestLast(pickingRow?.parsed?.last || '');
    setGuestGender('');
    setEloExpanded(false);
    setShowEloGuide(false);
    const elo = clubId
      ? await fetchClubDefaultStartingElo(clubId)
      : ELO_CONSTANTS.DEFAULT_STARTING_ELO;
    setClubDefaultElo(elo);
    setGuestMixedElo(elo);
    setGuestLevelsElo(elo);
    setStep('createGuest');
  };

  const handleCreateGuest = async (event) => {
    event?.preventDefault?.();
    if (creatingGuest) return;
    const firstName = guestFirst.trim();
    if (!firstName) {
      setError('First name is required');
      return;
    }
    if (!guestGender) {
      setError('Gender is required');
      return;
    }
    const mixedElo = Number(guestMixedElo);
    const levelsElo = Number(guestLevelsElo);
    if (
      Number.isNaN(mixedElo) ||
      Number.isNaN(levelsElo) ||
      mixedElo < 800 ||
      mixedElo > 2000 ||
      levelsElo < 800 ||
      levelsElo > 2000
    ) {
      setError('Starting ELO must be between 800 and 2000');
      return;
    }
    if (!canCreateGuest) {
      setError('Club or host is missing, so a guest cannot be created.');
      return;
    }

    setCreatingGuest(true);
    setError(null);
    try {
      const guest = await createGuest(
        {
          firstName,
          lastName: guestLast.trim(),
          gender: guestGender,
          mixedElo,
          levelsElo,
        },
        clubId,
        currentUser.id,
        seasonId
      );
      setRosterPlayers((prev) => {
        if ((prev || []).some((player) => player.id === guest.id)) return prev;
        return [...(prev || []), guest];
      });
      handleSelectPlayer(guest);
    } catch (err) {
      console.error('[PasteRosterModal] Failed to create guest:', err);
      setError(err.message || 'Failed to create guest');
    } finally {
      setCreatingGuest(false);
    }
  };

  const openPickerForRow = (row) => {
    setError(null);
    setPickerSearch('');
    setPickingRowId(row.id);
    setStep('picker');
  };

  const handleClearRow = (rowId) => {
    setRows((prev) => clearPlayerForRow(prev, rowId));
  };

  const handleAccept = async () => {
    if (!writable.length || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const acceptResult = await onAccept?.(writable);
      setResult({
        addedCount: acceptResult?.addedCount ?? writable.length,
        confirmedCount: acceptResult?.confirmedCount ?? summary.confirmed,
        waitlistCount: acceptResult?.waitlistCount ?? summary.waitlist,
        alreadyCount: summary.alreadyOnSession + (acceptResult?.alreadyCount || 0),
        unmatchedCount: summary.needsMatch,
        blockedCount: summary.blocked,
      });
      setStep('done');
    } catch (err) {
      console.error('[PasteRosterModal] Failed to add players:', err);
      setError(err.message || 'Failed to add players');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const title =
    step === 'picker'
      ? `Match “${pickingRow?.parsed?.raw || 'name'}”`
      : step === 'createGuest'
        ? `Add “${pickingRow?.parsed?.raw || 'name'}” as guest`
        : step === 'done'
          ? 'Players added'
          : 'Paste player list';

  const fillViewport = step === 'review' || step === 'picker' || step === 'createGuest';
  const pastedGuestLabel = pickingRow?.parsed?.raw || '';
  const noPickerMatches =
    pickerFiltered.suggested.length === 0 && pickerFiltered.others.length === 0;
  const mixedEloNum = Number(guestMixedElo);
  const levelsEloNum = Number(guestLevelsElo);
  const eloIsDefault =
    !Number.isNaN(mixedEloNum) &&
    !Number.isNaN(levelsEloNum) &&
    mixedEloNum === clubDefaultElo &&
    levelsEloNum === clubDefaultElo;
  const eloSummary = eloIsDefault
    ? `${clubDefaultElo} default — expand to change`
    : `Mixed ${mixedEloNum}, Levels ${levelsEloNum}`;

  return (
    <>
    <Modal isOpen onClose={requestClose} closeOnBackdropClick={!submitting && !creatingGuest}>
      <ModalContent
        size="xlarge"
        padding={false}
        className={`flex min-h-0 flex-col overflow-hidden ${
          fillViewport
            ? 'h-[min(92dvh,calc(100dvh-2rem))] !max-h-[min(92dvh,calc(100dvh-2rem))] sm:h-[min(88vh,840px)] sm:!max-h-[min(88vh,840px)]'
            : ''
        }`}
      >
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="flex shrink-0 items-start justify-between gap-2 border-b border-border-light px-4 py-3 sm:px-5">
            <div className="min-w-0 pr-8">
              <h2 className="text-lg font-semibold text-text-primary">{title}</h2>
              {step === 'paste' ? (
                <p className="mt-0.5 text-sm text-text-secondary">
                  Paste names from a spreadsheet, one per line. Extra columns after the first are ignored.
                </p>
              ) : null}
              {step === 'review' ? (
                <p className="mt-0.5 text-sm text-text-secondary">
                  Review matches, then add everyone who is ready. Names without a player stay out.
                </p>
              ) : null}
              {step === 'createGuest' ? (
                <p className="mt-0.5 text-sm text-text-secondary">
                  This creates a club guest. They are added to the session when you add ready players.
                </p>
              ) : null}
            </div>
            <ModalCloseButton onClose={requestClose} className="static shrink-0" disabled={submitting || creatingGuest} />
          </div>

          {error ? (
            <div className="shrink-0 border-b border-danger/30 bg-danger-light px-4 py-2 sm:px-5">
              <p className="text-sm text-danger-dark">{error}</p>
            </div>
          ) : null}

          {step === 'paste' ? (
            <div className="flex min-h-0 flex-1 flex-col px-4 py-4 sm:px-5">
              <FormField
                label="Player names"
                helperText="Example: Damon Chow — or Chow, Damon. Duplicate lines are ignored."
                className="flex min-h-0 flex-1 flex-col"
              >
                <Textarea
                  value={pasteText}
                  onChange={(e) => setPasteText(e.target.value)}
                  rows={12}
                  placeholder={'Damon Chow\nJane Doe\nJohn Smith'}
                  aria-label="Paste player names, one per line"
                  className="min-h-[12rem] w-full flex-1"
                />
              </FormField>
              <div className="mt-4 flex justify-end gap-2">
                <Button variant="outline" size="small" onClick={requestClose}>
                  Cancel
                </Button>
                <Button type="button" variant="primary" size="small" onClick={handleReview}>
                  Review matches
                </Button>
              </div>
            </div>
          ) : null}

          {step === 'review' ? (
            <>
              <div className="shrink-0 border-b border-border-light bg-surface-subtle px-4 py-2 text-xs text-text-primary sm:px-5 sm:text-sm">
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  {mode === 'signup' ? (
                    <>
                      {capacity.signupSelection === 'host_select' ? (
                        <span>
                          <span className="font-semibold">Will add to the list:</span> {summary.waitlist}
                        </span>
                      ) : (
                        <>
                          <span>
                            <span className="font-semibold">Will confirm:</span> {summary.confirmed}
                          </span>
                          <span>
                            <span className="font-semibold">Waitlist:</span> {summary.waitlist}
                          </span>
                        </>
                      )}
                    </>
                  ) : (
                    <span>
                      <span className="font-semibold">Will add:</span> {summary.add}
                    </span>
                  )}
                  <span>
                    <span className="font-semibold">Already on session:</span> {summary.alreadyOnSession}
                  </span>
                  <span>
                    <span className="font-semibold">Need a match:</span> {summary.needsMatch}
                  </span>
                  {summary.blocked > 0 ? (
                    <span>
                      <span className="font-semibold">Blocked:</span> {summary.blocked}
                    </span>
                  ) : null}
                </div>
              </div>

              <ul className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
                {placedRows.map((row) => {
                  const selected = row.selectedPlayerId ? playersById[row.selectedPlayerId] : null;
                  return (
                    <ReviewRow
                      key={row.id}
                      row={row}
                      selected={selected}
                      mode={mode}
                      onChoose={() => openPickerForRow(row)}
                      onClear={() => handleClearRow(row.id)}
                    />
                  );
                })}
              </ul>

              <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-border-light px-4 py-3 sm:flex-row sm:justify-end sm:px-5">
                <Button variant="outline" size="small" onClick={() => setStep('paste')} disabled={submitting}>
                  Back
                </Button>
                <Button
                  variant="primary"
                  size="small"
                  onClick={handleAccept}
                  disabled={submitting || writable.length === 0}
                >
                  {submitting
                    ? 'Adding…'
                    : `Add ${writable.length} player${writable.length === 1 ? '' : 's'}`}
                </Button>
              </div>
            </>
          ) : null}

          {step === 'picker' && pickingRow ? (
            <>
              <div className="shrink-0 border-b border-border-light px-4 py-3 sm:px-5">
                <MobileSearchField
                  placeholder="Search club players…"
                  value={pickerSearch}
                  onChange={(e) => setPickerSearch(e.target.value)}
                  aria-label="Search club players"
                  className="w-full rounded-md border border-border-medium bg-field px-3 py-2 text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-3 sm:px-5">
                <div className="space-y-4">
                  {canCreateGuest ? (
                    <AddNewGuestRow
                      search={pastedGuestLabel}
                      onAddNewGuest={openCreateGuest}
                    />
                  ) : null}
                  {noPickerMatches ? (
                    <Text className="py-6 text-center text-sm text-text-secondary">
                      No club players match this search.
                    </Text>
                  ) : (
                    <>
                      {pickerFiltered.suggested.length > 0 ? (
                        <div>
                          <Text className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
                            Suggested
                          </Text>
                          <div className="space-y-2">
                            {pickerFiltered.suggested.map((player) => (
                              <PlayerPickRow key={player.id} player={player} onPick={handleSelectPlayer} />
                            ))}
                          </div>
                        </div>
                      ) : null}
                      <div>
                        <Text className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
                          {pickerFiltered.suggested.length > 0 ? 'All players' : 'Players'}
                        </Text>
                        <div className="space-y-2">
                          {pickerFiltered.others.map((player) => (
                            <PlayerPickRow key={player.id} player={player} onPick={handleSelectPlayer} />
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div className="flex shrink-0 justify-end border-t border-border-light px-4 py-3 sm:px-5">
                <Button
                  variant="outline"
                  size="small"
                  onClick={() => {
                    setPickerSearch('');
                    setPickingRowId(null);
                    setStep('review');
                  }}
                >
                  Back
                </Button>
              </div>
            </>
          ) : null}

          {step === 'createGuest' && pickingRow ? (
            <form onSubmit={handleCreateGuest} className="flex min-h-0 flex-1 flex-col">
              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-5">
                <div className="space-y-4">
                  <FormField label="First name" required>
                    <Input
                      value={guestFirst}
                      onChange={(e) => setGuestFirst(e.target.value)}
                      autoComplete="given-name"
                      disabled={creatingGuest}
                      aria-label="Guest first name"
                    />
                  </FormField>
                  <FormField label="Last name">
                    <Input
                      value={guestLast}
                      onChange={(e) => setGuestLast(e.target.value)}
                      autoComplete="family-name"
                      disabled={creatingGuest}
                      aria-label="Guest last name"
                    />
                  </FormField>
                  <FormField label="Gender" required>
                    <GenderPick
                      value={guestGender}
                      onChange={setGuestGender}
                      disabled={creatingGuest}
                    />
                  </FormField>
                  <div className="rounded-lg border border-border-medium overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setEloExpanded((open) => !open)}
                      className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left bg-surface-subtle/80 hover:bg-surface-subtle transition-colors dark:bg-white/[0.04] dark:hover:bg-white/[0.07]"
                      aria-expanded={eloExpanded}
                      aria-controls="paste-guest-elo-panel"
                      id="paste-guest-elo-toggle"
                      disabled={creatingGuest}
                    >
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-text-secondary">
                          Starting ELO
                        </span>
                        <span className="block text-xs text-text-muted truncate">
                          {eloSummary}
                        </span>
                      </span>
                      <span className="shrink-0 text-text-muted tabular-nums" aria-hidden>
                        {eloExpanded ? '−' : '+'}
                      </span>
                    </button>
                    {eloExpanded ? (
                      <div
                        id="paste-guest-elo-panel"
                        role="region"
                        aria-labelledby="paste-guest-elo-toggle"
                        className="space-y-3 border-t border-border-medium bg-surface px-3 py-3"
                      >
                        <div className="grid grid-cols-2 gap-3">
                          <FormField label="Mixed doubles">
                            <Input
                              type="number"
                              min="800"
                              max="2000"
                              value={guestMixedElo}
                              onChange={(e) => setGuestMixedElo(e.target.value)}
                              disabled={creatingGuest}
                              aria-label="Mixed doubles starting ELO"
                            />
                          </FormField>
                          <FormField label="Levels doubles">
                            <Input
                              type="number"
                              min="800"
                              max="2000"
                              value={guestLevelsElo}
                              onChange={(e) => setGuestLevelsElo(e.target.value)}
                              disabled={creatingGuest}
                              aria-label="Levels doubles starting ELO"
                            />
                          </FormField>
                        </div>
                        <p className="text-xs text-text-muted">
                          Default is {clubDefaultElo} (club setting). Range: 800–2000
                        </p>
                        {clubId ? (
                          <div>
                            {!showEloGuide ? (
                              <button
                                type="button"
                                onClick={() => setShowEloGuide(true)}
                                className="text-xs font-medium text-primary hover:text-primary-dark"
                                disabled={creatingGuest}
                              >
                                Use club leaderboard as ELO guide →
                              </button>
                            ) : (
                              <div className="rounded-lg border border-border-medium bg-surface-subtle p-3">
                                <div className="mb-2 flex items-center justify-between gap-2">
                                  <p className="text-xs font-medium text-text-secondary">
                                    Club leaderboard — guide for starting ELO
                                  </p>
                                  <button
                                    type="button"
                                    onClick={() => setShowEloGuide(false)}
                                    className="shrink-0 text-xs text-text-muted hover:text-text-primary"
                                  >
                                    Hide
                                  </button>
                                </div>
                                <ClubLeaderboardPreview
                                  clubId={clubId}
                                  seasonId={seasonId}
                                  limit={8}
                                  levelsGender={
                                    guestGender === 'female'
                                      ? 'female'
                                      : guestGender === 'male'
                                        ? 'male'
                                        : null
                                  }
                                  compact
                                />
                              </div>
                            )}
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
              <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-border-light px-4 py-3 sm:flex-row sm:justify-end sm:px-5">
                <Button
                  type="button"
                  variant="outline"
                  size="small"
                  onClick={() => {
                    setError(null);
                    setStep('picker');
                  }}
                  disabled={creatingGuest}
                >
                  Back
                </Button>
                <Button type="submit" variant="primary" size="small" disabled={creatingGuest}>
                  {creatingGuest ? 'Creating…' : 'Create guest'}
                </Button>
              </div>
            </form>
          ) : null}

          {step === 'done' && result ? (
            <div className="px-4 py-4 sm:px-5">
              <div className="rounded-lg border border-border-medium bg-surface-subtle px-3 py-3 text-sm text-text-primary">
                <p>
                  Added {result.addedCount} player{result.addedCount === 1 ? '' : 's'}
                  {mode === 'signup' && result.confirmedCount + result.waitlistCount > 0
                    ? ` (${result.confirmedCount} confirmed, ${result.waitlistCount} waitlist)`
                    : ''}
                  .
                </p>
                {result.alreadyCount > 0 ? (
                  <p className="mt-1 text-text-secondary">
                    {result.alreadyCount} already on this session.
                  </p>
                ) : null}
                {result.unmatchedCount > 0 ? (
                  <p className="mt-1 text-text-secondary">
                    {result.unmatchedCount} still need a match and were not added.
                  </p>
                ) : null}
                {result.blockedCount > 0 ? (
                  <p className="mt-1 text-text-secondary">
                    {result.blockedCount} could not be added because the session is full.
                  </p>
                ) : null}
              </div>
              <div className="mt-4 flex justify-end">
                <Button variant="primary" size="small" onClick={requestClose}>
                  Done
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </ModalContent>
    </Modal>
    <ConfirmationModal
      isOpen={showDiscardConfirm}
      onClose={() => setShowDiscardConfirm(false)}
      onConfirm={confirmDiscardAndClose}
      title="Leave unmatched names?"
      message={`${summary.needsMatch} name${summary.needsMatch === 1 ? '' : 's'} still need a match and will be discarded if you close.`}
      confirmText="Discard names"
      cancelText="Keep matching"
      isDangerous
    />
    </>
  );
};

export default PasteRosterModal;
